"use client";

import Image, { type StaticImageData } from "next/image";
import { useRef } from "react";
import { A11y, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import Bg_Section from "~/public/assets/backgrounds/bg-section-bottom.png";
import type { ServicePageContent } from "../../../types/types";
import { useServiceCardsAnimation } from "./use-service-cards-animation";
import "swiper/css";
import "swiper/css/navigation";
import { ArrowLeft, ArrowRight } from "lucide-react";
import type { SolutionPageContent } from "@/app/servicos/modernizacao-de-sistemas-legados/content/content";
import { IconBox } from "@/components/icon-box";

type ServicePageTemplateProps = {
	service: ServicePageContent | SolutionPageContent;
};

type PhilosophysCardsProps = {
	icon: StaticImageData;
	title: string;
	description: string;
	iconVariant?: ServicePageContent["iconVariant"];
};

function PhilosophysCards({
	icon,
	title,
	description,
	iconVariant,
}: PhilosophysCardsProps) {
	return (
		<div
			key={title}
			data-service-card
			className="h-full bg-gray-1100/70 rounded-2xl py-13 px-11 text-center relative isolate"
		>
			{iconVariant === "outline" ? (
				<IconBox
					data-service-card-media
					bordered
					icon={icon}
					className="mx-auto"
				/>
			) : (
				<Image
					data-service-card-media
					src={icon}
					alt={title}
					width={200}
					height={200}
					className="h-14 w-14 mx-auto"
				/>
			)}
			<h3 className="mt-6 text-xl font-medium">{title}</h3>
			<p className="mt-6 text-white/50">{description}</p>
		</div>
	);
}

export default function PhilosophysServicesSection({
	service,
}: ServicePageTemplateProps) {
	const sectionRef = useRef<HTMLElement | null>(null);
	// `SolutionPageContent` não tem `iconVariant`; só as páginas de serviço.
	const iconVariant =
		"iconVariant" in service ? service.iconVariant : undefined;

	useServiceCardsAnimation(sectionRef, {
		preset: "drift",
	});

	return (
		<section
			ref={sectionRef}
			className="relative border-y border-gray-300 text-white "
		>
			<Image
				src={Bg_Section}
				alt="Background decorativo"
				className="pointer-events-none absolute bottom-0 h-full max-h-150 w-full object-cover opacity-90"
				priority={false}
				sizes="100vw"
			/>

			<div className="md:container-padding relative">
				<div
					data-service-cards-animation-trigger
					className="container relative md:border-x md:border-gray-300 px-6 lg:px-18 3xl:px-24! pt-24 pb-32"
				>
					<span className="hidden md:inline-flex absolute -top-1.75 -left-1.75 h-3 w-3 border border-gray-300 bg-gray-1000" />
					<span className="hidden md:inline-flex absolute -top-1.75 -right-1.75 h-3 w-3 border border-gray-300 bg-gray-1000" />

					<div className="flex justify-between gap-0 lg:gap-10 lg:items-end max-lg:flex-col">
						<div data-service-card className="max-w-132.5">
							<p className="tag">{service.philosophy?.tag}</p>
							<h2 className="title whitespace-pre-line">
								{service.philosophy?.title}
							</h2>
						</div>

						<div
							data-service-card
							className="max-lg:mt-6 description max-w-86.5 lg:text-right leading-[180%]"
						>
							{service.philosophy?.rightDescriptionBottom && (
								<p>{service.philosophy.rightDescriptionBottom}</p>
							)}
						</div>
					</div>

					<div className="block lg:hidden">
						<Swiper
							modules={[Navigation, A11y]}
							navigation={{
								nextEl: ".philosophy-button-next",
								prevEl: ".philosophy-button-prev",
							}}
							spaceBetween={16}
							slidesPerView="auto"
							className="mt-16 overflow-visible!"
						>
							{service.philosophy?.cards?.map((card) => (
								<SwiperSlide
									key={card.icon.src}
									className="max-w-[94%] h-auto!"
								>
									<PhilosophysCards
										icon={card.icon}
										title={card.title}
										description={card.description}
										iconVariant={iconVariant}
									/>
								</SwiperSlide>
							))}
						</Swiper>

						<div className="mt-5 flex items-center gap-5">
							<button
								aria-label="Item anterior"
								className="philosophy-button-prev cursor-pointer text-white transition-all duration-300 ease-in-out"
								type="button"
							>
								<ArrowLeft
									className="size-9 text-primary-300"
									strokeWidth={1.5}
								/>
							</button>
							<button
								aria-label="Próximo item"
								className="philosophy-button-next cursor-pointer text-white transition-all duration-300 ease-in-out"
								type="button"
							>
								<ArrowRight
									className="size-9 text-primary-300"
									strokeWidth={1.5}
								/>
							</button>
						</div>
					</div>

					{service.philosophy?.cards && (
						<div className="mt-16 hidden lg:grid grid-cols-1 gap-4 justify-between lg:grid-cols-3">
							{service.philosophy.cards.map((card) => (
								<PhilosophysCards
									key={card.icon.src}
									icon={card.icon}
									title={card.title}
									description={card.description}
									iconVariant={iconVariant}
								/>
							))}
						</div>
					)}
				</div>
			</div>
		</section>
	);
}
